import { Component, Input, computed, signal, OnChanges, SimpleChanges } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import {
    validatePassword,
    passwordStrength,
    PasswordPolicyErrors,
} from './password-policy.validator';
import { PASSWORD_POLICY } from './password-policy';

interface RequirementItem {
    label: string;
    met:   boolean;
}

@Component({
    selector: 'app-password-strength',
    standalone: true,
    imports: [MatIconModule],
    template: `
        <div class="password-strength">
        
          <!-- Barra de fuerza -->
          <div class="strength-bar">
            <div class="strength-segment"
              [class.filled]="strength() >= 1"
            [style.background]="strength() >= 1 ? barColor() : null"></div>
            <div class="strength-segment"
              [class.filled]="strength() >= 2"
            [style.background]="strength() >= 2 ? barColor() : null"></div>
            <div class="strength-segment"
              [class.filled]="strength() >= 3"
            [style.background]="strength() >= 3 ? barColor() : null"></div>
            <div class="strength-segment"
              [class.filled]="strength() >= 4"
            [style.background]="strength() >= 4 ? barColor() : null"></div>
          </div>
        
          @if (password) {
            <p class="strength-label" [style.color]="barColor()">
              {{ strengthLabel() }}
            </p>
          }
        
          <!-- Checklist de requisitos -->
          @if (password) {
            <ul class="requirements">
              @for (req of requirements(); track req) {
                <li [class.met]="req.met">
                  <mat-icon>{{ req.met ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                  <span>{{ req.label }}</span>
                </li>
              }
            </ul>
          }
        </div>
        `,
    styles: [`
        :host {
            display: block;
            margin: 4px 0 12px;
        }

        .password-strength { font-family: inherit; }

        .strength-bar {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 4px;
            margin-bottom: 6px;
        }

        .strength-segment {
            height: 4px;
            background: #e2e8f0;
            border-radius: 2px;
            transition: background 0.2s ease;
        }

        .strength-label {
            margin: 0 0 8px;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            text-transform: uppercase;
        }

        .requirements {
            list-style: none;
            padding: 8px 12px;
            margin: 0;
            background: #f8fafc;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px 12px;
        }

        .requirements li {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.72rem;
            color: #999999;
            line-height: 1.3;
            transition: color 0.15s ease;

            mat-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                flex-shrink: 0;
                color: #cbd5e1;
                transition: color 0.15s ease;
            }
        }

        .requirements li.met {
            color: #14853d;

            mat-icon {
                color: #1bb152;
            }
        }

        @media (max-width: 480px) {
            .requirements {
                grid-template-columns: 1fr;
            }
        }
    `],
})
export class PasswordStrengthComponent implements OnChanges {
    /** La contraseña a evaluar */
    @Input() password = '';

    /** Email del usuario (para detectar si la contraseña lo contiene) */
    @Input() email = '';

    /** Nombre del usuario (mismo) */
    @Input() name = '';

    private _errors = signal<PasswordPolicyErrors>({});
    strength = signal<number>(0);

    ngOnChanges(_changes: SimpleChanges): void {
        const result = validatePassword(this.password, {
            email: this.email,
            name:  this.name,
        });
        this._errors.set(result.errors);
        this.strength.set(passwordStrength(this.password));
    }

    /**
     * Lista de requisitos a mostrar. Un requisito está "met" cuando NO está
     * presente en errors.
     */
    requirements = computed<RequirementItem[]>(() => {
        const errs = this._errors();
        return [
            { label: `Mínimo ${PASSWORD_POLICY.minLength} caracteres`,
              met: !errs.tooShort && !errs.tooLong },
            { label: 'Una mayúscula (A-Z)',
              met: !errs.noUppercase },
            { label: 'Una minúscula (a-z)',
              met: !errs.noLowercase },
            { label: 'Un número (0-9)',
              met: !errs.noNumber },
            { label: 'Un símbolo (!@#$...)',
              met: !errs.noSymbol },
            { label: 'Sin caracteres repetidos',
              met: !errs.repeatedChars },
            { label: 'Sin secuencias (abc, 123)',
              met: !errs.hasSequence },
            { label: 'No es una contraseña común',
              met: !errs.isCommon },
            { label: 'No contiene tu correo',
              met: !errs.containsEmail },
            { label: 'No contiene tu nombre',
              met: !errs.containsName },
        ];
    });

    strengthLabel = computed(() => {
        switch (this.strength()) {
            case 0: return '';
            case 1: return 'Muy débil';
            case 2: return 'Débil';
            case 3: return 'Buena';
            case 4: return 'Fuerte';
            default: return '';
        }
    });

    barColor = computed(() => {
        switch (this.strength()) {
            case 1: return '#ed002a';   // rojo
            case 2: return '#cf5009';   // naranja
            case 3: return '#cf5009';   // ámbar
            case 4: return '#1bb152';   // verde
            default: return '#e2e8f0';
        }
    });
}